import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Zap, Users, Building2, History, Target, ChevronRight } from "lucide-react";
import { formatTime, formatDate } from "./calendarFormat";
import { BriefDetails } from "./BriefDetails";

const QuickStat = ({ icon: Icon, color = "", children }) => (
  <span className={`flex items-center gap-1 ${color}`}><Icon className="h-3 w-3" /> {children}</span>
);

export const MeetingBriefCard = ({ brief, index, isExpanded, onToggle }) => {
  const startTime = brief.start_time ? new Date(brief.start_time) : null;
  const isToday = startTime && new Date().toDateString() === startTime.toDateString();
  const hasAIContent = brief.executive_summary || brief.consultative_pov || brief.talking_points?.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index, 10) * 0.1 }}
      className="glass-card-light rounded-xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      data-testid={`brief-card-${brief.meeting_id || index}`}
    >
      <div className="p-4 cursor-pointer hover:bg-white/5 transition-colors" onClick={onToggle}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {isToday && <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">Today</Badge>}
              {hasAIContent && <Badge className="bg-gray-600/20 text-gray-400 border-gray-600/30 text-xs"><Zap className="h-3 w-3 mr-1" /> AI Brief Ready</Badge>}
              <span className="text-xs text-gray-500">{formatDate(brief.start_time)} at {formatTime(brief.start_time)}</span>
            </div>
            <h3 className="text-white text-sm mb-2">{brief.title}</h3>
            {brief.executive_summary && !isExpanded && <p className="text-gray-400 text-xs line-clamp-2 mb-2">{brief.executive_summary}</p>}
            <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
              {brief.attendees?.length > 0 && <QuickStat icon={Users}>{brief.attendees.length} attendee{brief.attendees.length !== 1 ? "s" : ""}</QuickStat>}
              {brief.companies?.length > 0 && <QuickStat icon={Building2}>{brief.companies.map((c) => c.name).join(", ")}</QuickStat>}
              {brief.previous_meeting_count > 0 && <QuickStat icon={History} color="text-emerald-400">{brief.previous_meeting_count} previous</QuickStat>}
              {brief.deal_strategy && <QuickStat icon={Target} color="text-amber-400">{brief.deal_strategy.split(" ").slice(0, 2).join(" ")}</QuickStat>}
            </div>
          </div>
          <motion.div animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="border-t border-white/10">
            <BriefDetails brief={brief} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
